import Tooltip from '@mui/material/Tooltip';
import DropdownTitle from 'components/DropdownTitle';
import EditableTitle, { useEditableTitle } from 'components/EditableTitle';
import { BackButtonContext } from 'contexts/BackButtonContext';
import { useContext, useEffect } from 'react';

import Header from '../Header';
import BackButton from './BackButton';
import BookmarkButton from './BookmarkButton';
import DocumentationButton, { DocumentPath } from './DocumentationButton';
import { useDocumentationLinkContext } from './DocumentationLinkProvider';
import { EditButton } from './EditButton';
import ExternalLinkButton from './ExternalLinkButton/ExternalLinkButton';
import { SaveButton } from './SaveButton';
import { PageHeaderProps } from './types';
import WorkspaceButton from './WorkspaceButton';

const noop = () => null;

const DynamicDocumentationButton = ({
  documentLink,
}: {
  documentLink?: keyof typeof DocumentPath;
}) => {
  const { link, key } = useDocumentationLinkContext() ?? {};
  const docLink = documentLink ? DocumentPath[documentLink] : undefined;
  return (
    <span
      style={{ display: 'contents' }}
      onMouseEnter={(e) => {
        if (!(process.env.NODE_ENV === 'development')) {
          return;
        }
        if (e.ctrlKey && e.altKey) {
          const docsKey = key ?? documentLink;
          // on super secret hotkey, tell me the documentation key
          console.log({ docsKey });
          if (docsKey) {
            navigator.clipboard.writeText(docsKey);
          }
        }
      }}
    >
      <DocumentationButton document={link ?? docLink} />
    </span>
  );
};

const PageHeader = (props: PageHeaderProps) => {
  const {
    bookmarkTitle,
    backButton,
    customTitle,
    title,
    onChange = noop,
    addonsRight,
    addonsLeft,
    expanded,
    subtitle,
    dropDownTitleOptions,
    dropdownTitleWidth,
    document: documentLink,
    classes,
  } = props;
  const editableTitleProps = useEditableTitle({ title, onSave: onChange });
  const { isShown } = useContext(BackButtonContext);

  useEffect(() => {
    if (!title) {
      document.title = 'Kinetic';
    } else {
      document.title = `${title}`;
    }
    return () => {
      document.title = 'Kinetic';
    };
  }, [title]);

  const renderPageHeader = () => {
    return (
      <Header
        addonsTop={<BackButton wasShown={isShown} isShown={!!backButton} />}
        customTitle={customTitle}
        title={
          <Tooltip title={title}>
            <div className={classes?.headerTitleWrapper}>
              {dropDownTitleOptions ? (
                <DropdownTitle
                  title={title}
                  onChange={onChange}
                  dropDownOptions={dropDownTitleOptions}
                  dropdownTitleWidth={dropdownTitleWidth}
                />
              ) : props.onChange ? (
                <EditableTitle {...editableTitleProps} />
              ) : (
                title
              )}
            </div>
          </Tooltip>
        }
        addonsRight={
          <>
            {addonsRight}
            <WorkspaceButton pageTitle={title} />
            <BookmarkButton title={bookmarkTitle ?? title} />
            <ExternalLinkButton documentPath={documentLink} />
            <DynamicDocumentationButton documentLink={documentLink} />
          </>
        }
        addonsLeft={addonsLeft}
        expanded={expanded}
        subtitle={subtitle}
      />
    );
  };

  return renderPageHeader();
};

PageHeader.SaveButton = SaveButton;
PageHeader.EditButton = EditButton;

export default PageHeader;
